// Components
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import LoadingWrapper from 'components/LoadingWrapper';
import TreeComponent from 'components/TreeComponent';
import { useDefaultTableProps } from 'hooks/useDefaultTableProps';
import { useDeleteConfirmation } from 'hooks/useDeleteConfirmation';
import { useExpandable } from 'hooks/useExpandable';
import { useRowProps } from 'hooks/useRowProps';
import { useTableTitle } from 'hooks/useTableTitle';
import { getText } from 'i18n/intl';
import { clone, get, isEmpty } from 'lodash';
import DrawerObjectTypes from 'pages/configuration/objectTypes/ObjectTypesDrawer';
// Slices
import { fetchFields, fetchFieldsGlobalOrder } from 'slices/field.slice';
import {
  addNewObjectTypeLocal,
  getDrawerData,
  getListIdSelected,
  requestDeleteObjectTypes,
  requestExternalOwners,
  requestMoveObjectType,
  requestObjectTypes,
  requestObjectTypesDetail,
  selectObjectTypesList,
  selectObjectTypesLoading,
  selectObjectTypesMap,
  updateDrawerObjectType,
} from 'slices/objectTypes.slice';
import { fetchOrganizationHierarchy } from 'slices/organizationHierarchy.slice';
import {
  fetchReservationTemplates,
  reservationTemplatesSelector,
  selectedReservationTemplateMap,
} from 'slices/reservationTemplate.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import type { TObjectTypesDetail } from 'types/state.interface';
// Types
// Utils
import {
  filterObjectTypesItem,
  handleCombineObjectTypesWithFilter,
  ynOptions,
} from 'utils/objectTypes';
import { countTreeNodes } from 'utils/treeData';
import {
  Button,
  DefaultContentLayout,
  EditableTable,
  Filter,
  Icon,
} from '@timeedit/ui-components';
import { type MenuProps, Popover } from '@timeedit/ui-components/antd';

const Table = memo(EditableTable);

const ObjectTypes = () => {
  const dispatch = useAppDispatch();

  // ------- selector ------------
  const reservationTemplateMap = useAppSelector(selectedReservationTemplateMap); //   data reservation Template Mapped by id;
  const objectTypesMap = useAppSelector(selectObjectTypesMap);
  const objectTypesList = useAppSelector(selectObjectTypesList);
  const loading = useAppSelector(selectObjectTypesLoading);
  const reservationTemplate = useAppSelector(reservationTemplatesSelector);
  const highlightIdsSelect = useAppSelector(getListIdSelected);

  // ------- state ------------
  const { expandedRowKeys, setExpandedRowKeys, expandable } =
    useExpandable<number>();
  const [highlightIds, setHighlightIds] = useState<any[]>([]);
  const [filter, setFilter] = useState<any>({
    filters: {},
    freeTextSearch: '',
    sorts: {},
  });
  const [confirmDelete, delContextHolder] = useDeleteConfirmation();

  // ------- Memo ------------
  const dataFiltered = useMemo(() => {
    if (!isEmpty(objectTypesList)) {
      const { freeTextSearch, filters = {} } = filter;
      if (
        freeTextSearch ||
        filters?.reservation_template ||
        typeof filters?.active === 'boolean'
      ) {
        const { dataFilter: dataResult } = handleCombineObjectTypesWithFilter(
          objectTypesList,
          (val: any) =>
            filterObjectTypesItem(
              {
                freeTextSearch,
                reservationTemplate: filters.reservation_template,
                active: filters.active,
              },
              val
            )
        );
        return dataResult;
      }

      const { dataFilter } =
        handleCombineObjectTypesWithFilter(objectTypesList);
      return dataFilter;
    }
    return [];
  }, [objectTypesList, filter]);

  const treeData = useMemo(() => {
    const { dataFilter, listParentIds } =
      handleCombineObjectTypesWithFilter(objectTypesList);
    setExpandedRowKeys(listParentIds);
    return dataFilter;
  }, [objectTypesList]);

  const filterOptions = useMemo(() => {
    const listOptions = clone(reservationTemplate)
      .sort((a: any, b: any) => {
        try {
          return a?.name?.localeCompare(b?.name);
        } catch (error) {
          return 0;
        }
      })
      .map((i: any) => {
        return {
          label: i.name,
          value: i.id,
        };
      });
    return {
      reservation_template: listOptions,
      active: ynOptions,
    };
  }, [reservationTemplate]);

  const columns = useMemo(
    () => renderObjectTypesColumn({ reservationTemplateMap, objectTypesMap }),
    [reservationTemplateMap, objectTypesMap]
  );

  useEffect(() => {
    setTimeout(() => {
      setHighlightIds(highlightIdsSelect);
    }, 100);
  }, [highlightIdsSelect.join('')]);

  // ------- functions ------------
  const onClickAddNew = useCallback(() => {
    dispatch(addNewObjectTypeLocal(null));
  }, [dispatch]);

  const setFirstDrawer = useCallback((data: TObjectTypesDetail) => {
    dispatch(updateDrawerObjectType({ isFirstDrawer: true, data }));
  }, []);

  const setSecondDrawer = useCallback((data: TObjectTypesDetail) => {
    dispatch(updateDrawerObjectType({ isFirstDrawer: false, data }));
  }, []);

  const onRowClick = useCallback(
    (record: TObjectTypesDetail) => {
      const firstDrawer = dispatch(getDrawerData(true));

      if (firstDrawer && get(firstDrawer, 'id') === record.id) {
        setFirstDrawer(null);
      } else if (record?.id) {
        dispatch(requestObjectTypesDetail(true, record));
      }
    },
    [objectTypesMap, dispatch]
  );

  const onRow = useRowProps(onRowClick, highlightIds);

  const onCompare = useCallback((record: TObjectTypesDetail) => {
    const firstDrawer = dispatch(getDrawerData(true));
    if (firstDrawer && get(firstDrawer, 'id')) {
      setSecondDrawer(record);
    } else {
      setFirstDrawer(record);
    }
  }, []);

  const onCreateNew = useCallback((record: TObjectTypesDetail) => {
    dispatch(addNewObjectTypeLocal(record));
  }, []);

  const onDelete = useCallback(
    (record: TObjectTypesDetail) => {
      confirmDelete(record?.name, () => {
        dispatch(requestDeleteObjectTypes([record?.id]));
      });
    },
    [confirmDelete, dispatch]
  );

  const onMove = useCallback((record: TObjectTypesDetail, destination: any) => {
    dispatch(requestMoveObjectType(record?.id, destination[0]));
  }, []);

  const onExpandAll = useCallback(
    (record: any) => {
      const listChild = getNestedChildIds(record, objectTypesMap);
      const newExpanded = Array.from(
        new Set([...listChild, ...expandedRowKeys])
      );
      setExpandedRowKeys(newExpanded);
    },
    [expandedRowKeys]
  );

  const onCollapseAll = useCallback(
    (record: any) => {
      const listChild = getNestedChildIds(record, objectTypesMap);
      const newExpanded = expandedRowKeys.filter((i) => !listChild.includes(i));
      setExpandedRowKeys(newExpanded);
    },
    [expandedRowKeys]
  );

  const menuProps: (record: TObjectTypesDetail) => MenuProps = useCallback(
    (record: TObjectTypesDetail) => {
      const expandOptions = renderExpandableOptions(record);

      const firstDrawer = dispatch(getDrawerData(true));
      return {
        items: [
          {
            key: 'title',
            label: record.name,
            disabled: true,
          },
          {
            type: 'divider',
          },
          {
            key: 'compare',
            label: firstDrawer ? 'Compare' : 'Open',
            icon: (
              <Icon
                id="EyeOutlined"
                alt="Eye"
                data-testid="menu-item-compare"
              />
            ),
          },
          {
            key: 'create',
            label: 'Create new',
            icon: (
              <Icon
                id="FileAddOutlined"
                alt="File Add"
                data-testid="menu-item-create"
              />
            ),
          },
          {
            key: 'move',
            label: (
              <Popover
                zIndex={10000}
                placement="right"
                content={
                  <TreeComponent
                    onSelect={(itemSelect: any) => onMove(record, itemSelect)}
                    treeData={treeData}
                  />
                }
              >
                <div
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                  data-testid="move-container"
                >
                  Move
                  <Icon
                    id="RightOutlined"
                    alt="Right"
                    style={{ fontSize: 12 }}
                  />
                </div>
              </Popover>
            ),
            icon: <Icon id="SwapOutlined" alt="Swap" />,
          },
          ...expandOptions,
          {
            key: 'delete',
            label: <span style={{ color: 'red' }}>Delete</span>,
            icon: (
              <Icon
                id="DeleteOutlined"
                alt="Delete"
                style={{ color: 'red' }}
                data-testid="menu-item-delete"
              />
            ),
          },
        ],
        onClick: (info) => {
          info.domEvent.stopPropagation();
          switch (info.key) {
            case 'expandAll':
              onExpandAll(record);
              break;
            case 'collapseAll':
              onCollapseAll(record);
              break;
            case 'compare':
              onCompare(record);
              break;
            case 'create':
              onCreateNew(record);
              break;
            case 'delete':
              onDelete(record);
              break;
            default:
              break;
          }
        },
      };
    },
    [
      onCollapseAll,
      onCompare,
      onCreateNew,
      onDelete,
      onExpandAll,
      onMove,
      treeData,
      dispatch,
    ]
  );
  // ------- Effect ------------
  useEffect(() => {
    dispatch(requestObjectTypes());
    dispatch(requestExternalOwners());
    dispatch(fetchFields());
    dispatch(fetchOrganizationHierarchy());
    dispatch(fetchReservationTemplates({ templateType: 'ALL' }));
    dispatch(fetchFieldsGlobalOrder());
  }, [dispatch]);

  const Footer = useCallback(
    () => (
      <div className="footer-action">
        <Button size="small" type="primary" onClick={onClickAddNew}>
          {getText('create_new')}
        </Button>
      </div>
    ),
    [onClickAddNew]
  );

  const filterTypesNum = useMemo(
    () => countTreeNodes(dataFiltered),
    [dataFiltered]
  );

  const title = useTableTitle<TObjectTypesDetail>({
    total: objectTypesList.length,
    filteredAmount: filterTypesNum,
  });

  const [contentRef, defaultTableProps] = useDefaultTableProps();

  return (
    <DefaultContentLayout
      title={getText('object_types')}
      subtitle={getText('object_types_subtitle')}
    >
      <div className="inner-content--wrapper">
        <div ref={contentRef} style={{ margin: '10px 5px 0px', width: '100%' }}>
          <Filter
            defaultFields={defaultFieldsFilter as any}
            onChange={(filterResult) => {
              setFilter(filterResult);
            }}
            optionalFields={[] as any}
            options={filterOptions as any}
            settings={filter as any}
          />
          <LoadingWrapper isLoading={loading}>
            <Table
              columns={columns}
              dataSource={dataFiltered as any}
              onRow={onRow}
              footer={Footer}
              getMenuProps={menuProps}
              expandable={expandable}
              title={title}
              {...defaultTableProps}
            />
          </LoadingWrapper>
          <DrawerObjectTypes />
        </div>
        {delContextHolder}
      </div>
    </DefaultContentLayout>
  );
};

export default ObjectTypes;

const defaultFieldsFilter = [
  {
    key: 'reservation_template',
    label: 'Reservation template',
    multiselect: false,
    type: 'text',
  },
  {
    key: 'active',
    label: 'Active',
    multiselect: false,
    type: 'text',
  },
];

const renderObjectTypesColumn = ({
  reservationTemplateMap,
  objectTypesMap,
}: {
  reservationTemplateMap: any;
  objectTypesMap: any;
}) => {
  return [
    {
      title: 'Type name',
      dataIndex: 'id',
      key: 'id',
      width: 300,
      render: (val: string) => {
        return (
          <div style={{ display: 'inline-block' }}>
            {get(objectTypesMap, `${val}.name`) || 'New object type'}
          </div>
        );
      },
    },
    {
      title: 'Reservation template',
      dataIndex: 'id',
      key: 'reservationTemplates',
      ellipsis: true,
      render: (value: number[]) => {
        const listIds: number[] = get(
          objectTypesMap,
          `${value}.reservationTemplates`
        );
        if (!listIds || listIds.length === 0) return null;
        return listIds
          .map((id) => {
            return get(reservationTemplateMap, [id, 'name'], '');
          })
          .filter(Boolean)
          .join(', ');
      },
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      width: 100,
      render: (val: string) => {
        return val === 'true' ? 'Yes' : 'No';
      },
    },
  ];
};
const options = [
  {
    key: 'expandAll',
    label: <span>Expand all below this node</span>,
    icon: <Icon id="ExpandAltOutlined" alt="Expand Alt" />,
  },
  {
    key: 'collapseAll',
    label: <span>Collapse all below this node</span>,
    icon: <Icon id="ShrinkOutlined" alt="Shrink" />,
  },
];

const renderExpandableOptions = (record: any) => {
  if (get(record, 'children.length', 0) > 0) {
    const haveChild = record.children.some(
      (item: any) => get(item, 'children.length', 0) > 0
    );
    return haveChild ? options : [];
  }
  return [];
};
const getNestedChildIds = (obj: any, objectTypesMap: any) => {
  const result: number[] = [];
  const listChild = obj?.children?.map((item: any) => item.id);
  getListChildId(listChild, objectTypesMap, result);
  return result;
};
const getListChildId = (
  listChild: any,
  objectTypesMap: any,
  result: number[]
) => {
  if (listChild) {
    result.push(...listChild);
    listChild.forEach((items: number) => {
      const data = objectTypesMap[items];

      if (data?.subTypes?.length > 0) {
        getListChildId(data?.subTypes, objectTypesMap, result);
      }
    });
  }
};
